import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';

import { carriesFiles, droppedMedia, type TransferMedia } from './media-transfer';

export interface FileDrop {
  /** True while files are over the region: what the affordance is drawn from. */
  active: boolean;
  /** Spread onto the element that accepts the files. */
  handlers: {
    onDragEnter: (event: DragEvent<HTMLElement>) => void;
    onDragOver: (event: DragEvent<HTMLElement>) => void;
    onDragLeave: (event: DragEvent<HTMLElement>) => void;
    onDrop: (event: DragEvent<HTMLElement>) => void;
  };
}

/**
 * Accepting files dragged onto a region.
 *
 * Three parts of this are the browser's doing rather than a choice. A drop only
 * happens where `dragover` was cancelled, so the region has to refuse the
 * default on every move. `dragenter` and `dragleave` bubble from every child
 * under the pointer, so crossing from one message to the next reads as leaving
 * the conversation unless the children that are still under the pointer are
 * tracked. And a drag that ends outside the window produces no drop at all, so
 * the affordance is also taken down by the events that say the drag is over.
 */
export function useFileDrop({
  disabled,
  onMedia,
}: {
  disabled: boolean;
  onMedia: (media: TransferMedia[]) => void;
}): FileDrop {
  const [active, setActive] = useState(false);
  /** The descendants the pointer is currently inside, not a count of them:
      browsers differ on how many enter and leave events one crossing produces. */
  const inside = useRef(new Set<EventTarget>());

  const finish = useCallback(() => {
    inside.current.clear();
    setActive(false);
  }, []);

  // A file dropped on the desktop, or a drag abandoned with Escape, leaves the
  // region without ever telling it. Without this the overlay would stay up over
  // a conversation nobody is dragging anything onto.
  useEffect(() => {
    if (!active) return;
    window.addEventListener('dragend', finish);
    window.addEventListener('drop', finish);
    return () => {
      window.removeEventListener('dragend', finish);
      window.removeEventListener('drop', finish);
    };
  }, [active, finish]);

  const onDragEnter = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (disabled || !carriesFiles(event.dataTransfer)) return;
      event.preventDefault();
      inside.current.add(event.target);
      setActive(true);
    },
    [disabled],
  );

  const onDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (disabled || !carriesFiles(event.dataTransfer)) return;
      // Without this the browser keeps the drop for itself and navigates to the
      // file, which throws away the drafts and the open conversation.
      event.preventDefault();
      // Says "copy" on the cursor: the file is not being moved out of its folder.
      event.dataTransfer.dropEffect = 'copy';
    },
    [disabled],
  );

  const onDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    inside.current.delete(event.target);
    // The element being entered. Still inside the region means the pointer
    // merely crossed between two children of it.
    const entering = event.relatedTarget;
    if (entering instanceof Node && event.currentTarget.contains(entering)) return;
    if (inside.current.size === 0) setActive(false);
  }, []);

  const onDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!carriesFiles(event.dataTransfer)) return;
      // Even when nothing may be attached: the default here is for the browser to
      // navigate to the file, and refusing that is the whole point.
      event.preventDefault();
      finish();
      if (disabled) return;
      onMedia(droppedMedia(event.dataTransfer));
    },
    [disabled, finish, onMedia],
  );

  return { active, handlers: { onDragEnter, onDragOver, onDragLeave, onDrop } };
}
